'use client';

import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { X, StickyNote, Plus } from 'lucide-react';

type PageType =
  | 'overview'
  | 'financials'
  | 'financials-income-statement'
  | 'financials-balance-sheet'
  | 'financials-cash-flow'
  | 'financials-growth-ratios'
  | 'dividends'
  | 'ratios'
  | 'segments'
  | 'historical-earnings'
  | 'news'
  | 'earnings-call';

interface Tab {
  symbol: string;
  companyName: string;
}

interface CompanyTabsProps {
  tabs: Tab[];
  activeTab: string | null;
  currentPage: PageType;
  onTabSelect(symbol: string): void;
  onTabRemove(symbol: string): void;
  onTabHover?: (symbol: string) => void;
  onNotesClick?: () => void;
  onAddTabClick?: () => void;
  children: React.ReactNode;
}

const getPagePath = (pageType: PageType): string => {
  switch (pageType) {
    case 'overview':
      return '';
    case 'financials':
      return '/financials';
    case 'financials-income-statement':
      return '/financials/income-statement';
    case 'financials-balance-sheet':
      return '/financials/balance-sheet';
    case 'financials-cash-flow':
      return '/financials/cash-flow';
    case 'financials-growth-ratios':
      return '/financials/growth-ratios';
    case 'dividends':
      return '/dividends';
    case 'ratios':
      return '/ratios';
    case 'segments':
      return '/segments';
    case 'historical-earnings':
      return '/historical-earnings';
    case 'news':
      return '/news';
    case 'earnings-call':
      return '/earnings-call';
    default:
      return '';
  }
};

export function CompanyTabs({
  tabs,
  activeTab,
  currentPage,
  onTabSelect,
  onTabRemove,
  onTabHover,
  onNotesClick,
  onAddTabClick,
  children,
}: CompanyTabsProps) {
  const router = useRouter();

  const handleTabSelect = (tabSymbol: string) => {
    const pagePath = getPagePath(currentPage);
    router.push(`/company/${tabSymbol}${pagePath}`);
    onTabSelect(tabSymbol);
  };

  const handleTabRemove = (tabSymbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onTabRemove(tabSymbol);

    // Navigate to another tab if we removed the current one
    if (tabSymbol === activeTab) {
      const remainingTabs = tabs.filter((tab) => tab.symbol !== tabSymbol);
      if (remainingTabs.length > 0) {
        const pagePath = getPagePath(currentPage);
        router.push(
          `/company/${remainingTabs[0].symbol}${pagePath}?from=tab-deletion`,
        );
      } else {
        router.push('/company');
      }
    }
  };

  const handleTabHover = (tabSymbol: string) => {
    if (onTabHover && tabSymbol !== activeTab) {
      onTabHover(tabSymbol);
    }
  };

  if (tabs.length === 0) {
    return <div className="space-y-6">{children}</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs
        value={activeTab || undefined}
        onValueChange={handleTabSelect}
        className="space-y-6"
      >
        <div className="border-b flex items-center">
          <div className="flex-1 flex overflow-x-auto">
            {tabs.map((tab) => (
              // biome-ignore lint/nursery/noStaticElementInteractions: sybau
              <div
                key={tab.symbol}
                className="relative flex items-center"
                onMouseEnter={() => handleTabHover(tab.symbol)}
              >
                <TabsList className="h-10 bg-transparent p-0">
                  <TabsTrigger
                    value={tab.symbol}
                    className="h-10 rounded-none border-b-2 border-b-transparent data-[state=active]:border-b-primary data-[state=active]:shadow-none pr-8"
                  >
                    {tab.symbol}
                  </TabsTrigger>
                </TabsList>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 size-5 p-0"
                  onClick={(e) => handleTabRemove(tab.symbol, e)}
                >
                  <X className="size-3" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center">
            {onNotesClick && (
              <Button
                variant="ghost"
                size="icon"
                className="size-10 shrink-0"
                onClick={onNotesClick}
                disabled={!activeTab}
                title="View notes"
              >
                <StickyNote className="size-4" />
              </Button>
            )}
            {onAddTabClick && (
              <Button
                variant="ghost"
                size="icon"
                className="size-10 shrink-0"
                onClick={onAddTabClick}
                title="Add new tab"
              >
                <Plus className="size-4" />
              </Button>
            )}
          </div>
        </div>

        {tabs.map((tab) => (
          <TabsContent key={tab.symbol} value={tab.symbol} className="mt-6">
            {activeTab === tab.symbol && children}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
